import { t } from '@lingui/core/macro';
import { BookingCarForCustomer, InspectionContext, type InspectionSessionRecord, TireSeason } from '@navio/nsdk';
import { BrandType } from 'noddi-types';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiEmptyContent } from 'noddi-ui-web';
import {
  isWheelSetCarWheelMeasurementValues,
  mapInspectionSessionsToMeasurements,
  TireMeasurementCard
} from 'noddi-web';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { getBrandSpecificContent } from './getBrandSpecificContent';
import { TireInformationButton } from './TireInformationButton';
import { WheelInspection } from './WheelInspection';

interface CarInspectionAndMeasurementProps {
  bookingCar: BookingCarForCustomer;
  sessions: InspectionSessionRecord[];
  brandDomain: BrandType;
}

export const CarInspectionAndMeasurement = ({
  bookingCar,
  sessions,
  brandDomain
}: CarInspectionAndMeasurementProps) => {
  const carWheelMeasurements = mapInspectionSessionsToMeasurements(sessions);

  const hasSummerTireMeasurements = Object.keys(carWheelMeasurements.summer).length > 0;
  const hasWinterTireMeasurements = Object.keys(carWheelMeasurements.winter).length > 0;
  const hasTireMeasurements = hasSummerTireMeasurements || hasWinterTireMeasurements;

  const hasInspection = sessions.some(
    (session) => session.context === InspectionContext.PRE_JOB || session.context === InspectionContext.POST_JOB
  );

  const items = getBrandSpecificContent(brandDomain);
  if (!hasTireMeasurements && !hasInspection) {
    return (
      <div>
        <NoddiEmptyContent title={t`What will info be available here?`}>
          <ul className='-mt-4 flex flex-col gap-3'>
            {items.map((item, index) => (
              <li key={index} className='flex gap-1.5 text-start text-balance'>
                <NoddiIcon name='Check' size='small' className='mt-0.5 shrink-0 text-emerald-500' />
                <span className='text-sm'>{item}</span>
              </li>
            ))}
          </ul>
        </NoddiEmptyContent>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4'>
      {hasTireMeasurements && (
        <>
          <TireInformationButton />
          <div className='grid grid-cols-1 gap-4 2xl:grid-cols-2'>
            {hasSummerTireMeasurements && isWheelSetCarWheelMeasurementValues(carWheelMeasurements.summer) && (
              <TireMeasurementCard
                tireSeason={TireSeason.SUMMER}
                thresholds={bookingCar.summerTreadDepthThresholds ?? null}
                measurements={carWheelMeasurements.summer}
                translations={getCommonTranslations().wheelMeasurements}
              />
            )}
            {hasWinterTireMeasurements && isWheelSetCarWheelMeasurementValues(carWheelMeasurements.winter) && (
              <TireMeasurementCard
                tireSeason={TireSeason.WINTER}
                thresholds={bookingCar.winterTreadDepthThresholds ?? null}
                measurements={carWheelMeasurements.winter}
                translations={getCommonTranslations().wheelMeasurements}
              />
            )}
          </div>
        </>
      )}

      {hasInspection && <WheelInspection sessions={sessions} />}
    </div>
  );
};
